import {
  DomainCode,
  Membership,
  PermissionCode,
  PermissionGrant,
  Scope,
} from "../model/access-control.types";

const scopeRank: Record<Scope, number> = {
  own: 0,
  hierarchy: 1,
  department: 2,
  all: 3,
};

export function flattenPermissions(
  memberships: Membership[],
): PermissionGrant[] {
  const permissionsByCode = new Map<PermissionCode, PermissionGrant>();

  for (const membership of memberships) {
    for (const permission of membership.permissions ?? []) {
      const current = permissionsByCode.get(permission.code);
      if (!current || scopeRank[permission.scope] > scopeRank[current.scope]) {
        permissionsByCode.set(permission.code, permission);
      }
    }
  }

  return [...permissionsByCode.values()].sort((left, right) =>
    left.code.localeCompare(right.code),
  );
}

export function createPermissionSet(
  permissions: PermissionGrant[],
): Set<PermissionCode> {
  return new Set(permissions.map((permission) => permission.code));
}

export function hasDomainAccessFromMemberships(
  memberships: Membership[],
  domain: DomainCode,
): boolean {
  return memberships.some((membership) => membership.domain === domain);
}

export function hasPermissionFromSet(
  permissionSet: Set<PermissionCode>,
  permission: PermissionCode,
): boolean {
  return permissionSet.has(permission);
}

export function buildPermissionCode({
  domain,
  resource,
  action,
}: {
  domain: DomainCode;
  resource: string;
  action: string;
}): PermissionCode {
  const normalizedResource = resource.trim().replace(/\s+/g, "");
  const normalizedAction = action.trim().replace(/\s+/g, "");
  return `${domain}.${normalizedResource}.${normalizedAction}` as PermissionCode;
}

export function getPermissionScopeFromPermissions(
  permissions: PermissionGrant[],
  permission: PermissionCode,
): Scope | undefined {
  return permissions.find((item) => item.code === permission)?.scope;
}
